import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { Trash2, Plus } from "lucide-react";
import {
  useTaskChecklist,
  useAddChecklistItem,
  useToggleChecklistItem,
  useDeleteChecklistItem,
} from "@/hooks/useTaskChecklist";

interface TaskChecklistProps {
  taskId: string;
}

export function TaskChecklist({ taskId }: TaskChecklistProps) {
  const { data: items = [], isLoading } = useTaskChecklist(taskId);
  const addItem = useAddChecklistItem();
  const toggleItem = useToggleChecklistItem();
  const deleteItem = useDeleteChecklistItem();
  const [newTitle, setNewTitle] = useState("");

  const done = items.filter((i) => i.completed).length;
  const total = items.length;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;

  const handleAdd = () => {
    if (!newTitle.trim()) return;
    addItem.mutate({ task_id: taskId, title: newTitle.trim() });
    setNewTitle("");
  };

  if (isLoading) return null;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between text-sm font-medium">
        <span>Checkliste</span>
        {total > 0 && (
          <span className="text-muted-foreground text-xs">
            {done}/{total} ({pct}%)
          </span>
        )}
      </div>
      {total > 0 && <Progress value={pct} className="h-1.5" />}
      <div className="space-y-1">
        {items.map((item) => (
          <div key={item.id} className="flex items-center gap-2 group">
            <Checkbox
              checked={item.completed}
              onCheckedChange={(checked) =>
                toggleItem.mutate({
                  id: item.id,
                  completed: !!checked,
                  task_id: taskId,
                })
              }
            />
            <span
              className={`text-sm flex-1 ${
                item.completed ? "line-through text-muted-foreground" : ""
              }`}
            >
              {item.title}
            </span>
            <Button
              size="icon"
              variant="ghost"
              className="h-6 w-6 opacity-0 group-hover:opacity-100"
              onClick={() => deleteItem.mutate({ id: item.id, task_id: taskId })}
            >
              <Trash2 className="h-3 w-3 text-destructive" />
            </Button>
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <Input
          placeholder="Neuer Checkpunkt..."
          value={newTitle}
          onChange={(e) => setNewTitle(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleAdd()}
          className="h-8 text-sm"
        />
        <Button size="sm" variant="outline" onClick={handleAdd} disabled={!newTitle.trim()}>
          <Plus className="h-3 w-3" />
        </Button>
      </div>
    </div>
  );
}
